import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { optionalSupabaseAuth } from "@/integrations/supabase/auth-middleware-optional";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

async function getUserCredits(userId: string): Promise<number> {
  const { data } = await supabaseAdmin
    .from("profiles")
    .select("credits")
    .eq("id", userId)
    .maybeSingle();
  return (data?.credits as number | null) ?? 0;
}

async function decrementUserCredits(userId: string): Promise<void> {
  await supabaseAdmin.rpc("increment_user_credits", {
    user_uuid: userId,
    amount: -1,
  });
}

const analyzeInput = z.object({
  text: z.string().min(40, "Paste at least 40 characters of contract text").max(40000),
  toolSlug: z.string().min(1).max(64).optional(),
  title: z.string().min(1).max(120).optional(),
  stateContext: z.string().optional(),
});

const SYSTEM_PROMPT = `You are legaldecoder, a friendly, plain-English contract translator for non-lawyers (renters, freelancers, small business owners). You DO NOT give legal advice — you explain what a document says and flag risks.

Always return a single JSON object that matches this exact schema:
{
  "verdict": { "summary": string (1-2 sentences, empathetic, plain English), "risk": "low" | "medium" | "high" },
  "riskScore": number (0-100, where 0 = totally safe, 100 = walk away),
  "keyTerms": string[] (3-6 bullet points: dates, amounts, durations, parties, obligations),
  "redFlags": string[] (0-6 short headlines of predatory/unusual/one-sided clauses; empty array if none),
  "clauseExplanations": [
    {
      "title": string (clause name in plain English, e.g. "Security Deposit"),
      "what": string (1-2 sentences: what this clause says),
      "why": string (1-2 sentences: why it matters / what to watch out for),
      "suggestion": string (1 sentence: what the user should do — accept, push back, negotiate, walk away),
      "negotiationScript": string (1-2 sentences: an exact script/template the user can copy-paste into an email to counter or push back on this clause. If no pushback is needed, leave empty string ""),
      "severity": "info" | "caution" | "warning"
    }
  ] (3-8 items covering the most important clauses)
}

Tone: friendly, reassuring, like a smart friend ("We've got your back"). Never invent facts not in the text. If the text is too short or unclear, say so in the verdict summary, set risk to "low", riskScore to 0, and return empty arrays.`;

export type AnalysisResult = {
  verdict: { summary: string; risk: "low" | "medium" | "high" };
  riskScore: number;
  keyTerms: string[];
  redFlags: string[];
  clauseExplanations: Array<{
    title: string;
    what: string;
    why: string;
    suggestion: string;
    negotiationScript?: string;
    severity: "info" | "caution" | "warning";
  }>;
};

export const analyzeContract = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => analyzeInput.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    // 1. Credits check — all authenticated users get full premium output
    const credits = await getUserCredits(userId);
    if (credits <= 0) {
      return {
        ok: false as const,
        reason: "quota_exceeded" as const,
        credits,
      };
    }

    // 2. Call Lovable AI Gateway
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");

    const dynamicPrompt = data.stateContext
      ? SYSTEM_PROMPT +
        `\n\nCRITICAL INSTRUCTION: The user is located in the US state of ${data.stateContext}. Please evaluate clauses (especially non-competes, late fees, security deposits, and evictions) against the general laws of ${data.stateContext}. Flag clauses that might be unenforceable in this state as "high" risk.`
      : SYSTEM_PROMPT;

    const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "openai/gpt-5-mini",
        messages: [
          { role: "system", content: dynamicPrompt },
          { role: "user", content: `Analyze this contract:\n\n${data.text}` },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!aiRes.ok) {
      const body = await aiRes.text();
      console.error("AI gateway error", { status: aiRes.status, body });
      if (aiRes.status === 429) return { ok: false as const, reason: "rate_limited" as const };
      if (aiRes.status === 402)
        return { ok: false as const, reason: "ai_credits_exhausted" as const };
      throw new Error("Analysis service unavailable");
    }

    const aiJson = await aiRes.json();
    const content = aiJson.choices?.[0]?.message?.content;
    if (!content) throw new Error("Empty AI response");

    let parsed: AnalysisResult;
    try {
      const raw = JSON.parse(content);
      parsed = {
        verdict: raw.verdict ?? { summary: "Unable to analyze.", risk: "low" },
        riskScore:
          typeof raw.riskScore === "number" ? Math.max(0, Math.min(100, raw.riskScore)) : 0,
        keyTerms: Array.isArray(raw.keyTerms) ? raw.keyTerms : [],
        redFlags: Array.isArray(raw.redFlags) ? raw.redFlags : [],
        clauseExplanations: Array.isArray(raw.clauseExplanations) ? raw.clauseExplanations : [],
      };
    } catch {
      throw new Error("AI returned malformed output");
    }

    // 3. Persist full analysis (all credit-paying users get history) and decrement credits.
    const row = {
      user_id: userId,
      status: "completed",
      risk_level: parsed.verdict?.risk ?? "low",
      risk_score: parsed.riskScore,
      tool_slug: data.toolSlug ?? null,
      title: data.title ?? deriveTitle(data.text),
      result: parsed,
    };
    const { data: inserted } = await supabaseAdmin
      .from("analyses")
      .insert([row])
      .select("id")
      .single();

    await decrementUserCredits(userId);

    return {
      ok: true as const,
      result: parsed,
      analysisId: (inserted?.id as string) ?? null,
      extractedText: data.text,
      creditsRemaining: Math.max(0, credits - 1),
    };
  });

function deriveTitle(text: string): string {
  const firstLine =
    text
      .split("\n")
      .find((l) => l.trim().length > 0)
      ?.trim() ?? "Contract";
  return firstLine.slice(0, 80);
}

export type UsageSnapshot = { credits: number } | null;

export const getMyUsage = createServerFn({ method: "GET" })
  .middleware([optionalSupabaseAuth])
  .handler(async ({ context }): Promise<UsageSnapshot> => {
    const { userId } = context;
    if (!userId) return null;
    const credits = await getUserCredits(userId);
    return { credits };
  });

// History — Pro only
export const listMyAnalyses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { data } = await supabaseAdmin
      .from("analyses")
      .select("id, title, risk_level, risk_score, tool_slug, created_at, result")
      .eq("user_id", userId)
      .not("result", "is", null)
      .order("created_at", { ascending: false })
      .limit(50);
    return data ?? [];
  });

export const getMyAnalysis = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { data: row } = await supabaseAdmin
      .from("analyses")
      .select("id, title, risk_level, risk_score, tool_slug, created_at, result")
      .eq("user_id", userId)
      .eq("id", data.id)
      .maybeSingle();
    return row;
  });

export const deleteMyAnalysis = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await supabaseAdmin.from("analyses").delete().eq("user_id", userId).eq("id", data.id);
    return { ok: true };
  });

// Comparison — Pro only
const compareInput = z.object({
  textA: z.string().min(40).max(40000),
  textB: z.string().min(40).max(40000),
  labelA: z.string().max(80).optional(),
  labelB: z.string().max(80).optional(),
});

const COMPARE_PROMPT = `You compare two contracts side-by-side for a non-lawyer. Return JSON:
{
  "summary": string (2-3 sentences: which contract is friendlier and why, plain English),
  "winner": "A" | "B" | "tie",
  "rows": [
    { "topic": string (e.g. "Security Deposit", "Termination", "Late Fees"),
      "a": string (what contract A says, plain English, ≤25 words),
      "b": string (what contract B says, plain English, ≤25 words),
      "verdict": "A" | "B" | "tie" }
  ] (5-10 rows covering the most important topics)
}
Be honest, friendly, never invent facts. If one contract is silent on a topic, say "Not addressed".`;

export const compareContracts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => compareInput.parse(input))
  .handler(async ({ data, context }) => {
    void context;
    // All authenticated users can compare contracts under the credit-pack model.
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");

    const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model: "openai/gpt-5-mini",
        messages: [
          { role: "system", content: COMPARE_PROMPT },
          {
            role: "user",
            content: `Contract A (${data.labelA ?? "A"}):\n${data.textA}\n\n---\n\nContract B (${data.labelB ?? "B"}):\n${data.textB}`,
          },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!aiRes.ok) {
      if (aiRes.status === 429) return { ok: false as const, reason: "rate_limited" as const };
      if (aiRes.status === 402)
        return { ok: false as const, reason: "ai_credits_exhausted" as const };
      throw new Error("Comparison service unavailable");
    }

    const aiJson = await aiRes.json();
    const content = aiJson.choices?.[0]?.message?.content;
    if (!content) throw new Error("Empty AI response");

    return { ok: true as const, result: JSON.parse(content) };
  });
